import { arrayOf, bool, func, shape, string } from "prop-types";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router-dom";
import { goBack } from "react-router-redux";
import classNames from "classnames";
import Yup from "yup";

import { DebinScheduledConfirmation, Box, Spinner } from "@ui-kit/components/index.es";
import Notification from "pages/_components/Notification";
import Head from "pages/_components/Head";

import * as i18n from "util/i18n";
import { getIncomingDebinAccountName } from "util/accounts";
import { actions as debinActions, selectors as debinSelectors } from "reducers/debin";
import { actions as tokenActions, selectors as tokenSelectors } from "reducers/token";

import isTokenActive from "util/token";
import { resizableRoute } from "pages/_components/Resizable";
import { applyEmbeddedV3ShellLayoutState, clearEmbeddedV3ShellLayoutState } from "util/v3ShellLayoutSync";

function DebinScheduledConfirmationPage({
    dispatch,
    debinToConfirm,
    accounts,
    loading,
    history,
    isDesktop,
    seller,
    tokenNumber,
    embeddedInV3Shell = false,
}) {
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!debinToConfirm) {
            history.replace("/v2/debin/scheduled");
        }
        if (!accounts.length) {
            dispatch(debinActions.debinPreActivity());
        }
        if (!isDesktop && isTokenActive()) {
            dispatch(tokenActions.softTokenGenerationRequest());
        }
    }, [debinToConfirm, accounts.length, dispatch, history, isDesktop]);

    const handleBack = () => {
        dispatch(goBack());
    };

    function getAccountDescriptionByCBU(cbu) {
        const account = accounts.find((obj) => obj.cbu === cbu);
        return getIncomingDebinAccountName(account);
    }

    const getToken = async () => {
        if (!isDesktop && isTokenActive() && tokenNumber) {
            return { token: tokenNumber };
        }
        return { token: null };
    };

    const mappedDebin = debinToConfirm
        ? {
              ...debinToConfirm.debin,
              autorizated: debinToConfirm.debin.autorizado,
              buyer: {
                  cbu: debinToConfirm.debin.comprador.cbu,
                  cuit: debinToConfirm.debin.comprador.cuit,
                  name: debinToConfirm.debin.comprador.nombre,
              },
              confirmation_date: debinToConfirm.debin.fecha_confirmacion,
              creation_date: debinToConfirm.debin.fecha_creacion,
              debin: {
                  amount: debinToConfirm.debin.debin.importe,
                  concept: debinToConfirm.debin.debin.concepto,
                  currency: debinToConfirm.debin.debin.moneda,
                  detail: debinToConfirm.debin.debin.detalle,
                  limit_part: debinToConfirm.debin.debin.limite_cuotas,
                  reference: debinToConfirm.debin.debin.referencia,
                  service: debinToConfirm.debin.debin.prestacion,
              },
              seller: {
                  cuit: debinToConfirm.debin.vendedor.cuit,
                  name: debinToConfirm.debin.vendedor.nombre,
              },
              status: debinToConfirm.debin.estado,
              debitAccount: getAccountDescriptionByCBU(debinToConfirm.debin.comprador.cbu),
          }
        : null;

    const partsList = mappedDebin
        ? Array.from({ length: mappedDebin.debin.limit_part }, (_, i) => ({
              number: i + 1,
              currency: mappedDebin.debin.currency === "032" ? "ARS" : "USD",
              amount: mappedDebin.debin.amount,
          }))
        : [];

    const getCardLabel = (id) => i18n.get(`debin.programmed.confirmation.card.${id}`);
    const getTableLabel = (id) => i18n.get(`debin.programmed.label.table.${id}`);

    const handleConfirm = ({ otp }) => {
        setSubmitting(true);
        const {
            prestacion,
            referencia,
            detalle,
            concepto,
            moneda,
            // eslint-disable-next-line camelcase
            limite_cuotas,
            importe,
        } = debinToConfirm.debin.debin;
        const { cbu } = debinToConfirm.debin.comprador;
        const debitAccount = accounts.find((a) => a.cbu === cbu);

        const finalOtp = !isDesktop && isTokenActive() && tokenNumber ? tokenNumber : otp;

        const recurrencyInfo = {
            _otp: finalOtp,
            prestacion,
            referencia,
            detalle,
            concepto,
            moneda,
            cbu,
            seller: debinToConfirm.debin.vendedor.cuit,
            action: debinToConfirm.action.toUpperCase(),
            id: debinToConfirm.debin.id,
            sellerName: seller?.titular,
            debitAccount,
            // eslint-disable-next-line camelcase
            limite_cuotas,
            monto: importe,
        };
        dispatch(debinActions.updateRecurrencyState(recurrencyInfo, setSubmitting));
    };

    const otpSchema = Yup.object({
        otp:
            !isDesktop && isTokenActive() && tokenNumber
                ? Yup.string().nullable()
                : Yup.string().required(i18n.get("debin.programmed.confirmation.otp.required")),
    });

    const confirmationProps = {
        title: i18n.get(`debin.recurrency.modal.${debinToConfirm?.action?.toLowerCase()}.title`),
        description: i18n.get(`debin.recurrency.modal.${debinToConfirm?.action?.toLowerCase()}.text`),
        handleConfirm,
        loading: submitting,
        confirmationText: i18n.get("debin.programmed.confirmation.confirmationText"),
        getToken,
        validationConfirm: otpSchema,
    };

    useEffect(() => {
        if (!embeddedInV3Shell) {
            return undefined;
        }
        const service = debinToConfirm?.debin?.debin?.prestacion;
        if (loading || !debinToConfirm || !service) {
            return () => {
                clearEmbeddedV3ShellLayoutState(dispatch);
            };
        }
        const headerTitle = `${i18n.get(
            `debin.programmed.confirmation.title.${debinToConfirm.action.toLowerCase()}`,
        )} ${service}`;
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            scopes: ["debin"],
            headerTitle,
            goBackPath: "/v2/debin/scheduled",
        });
        return () => {
            clearEmbeddedV3ShellLayoutState(dispatch);
        };
    }, [dispatch, embeddedInV3Shell, isDesktop, debinToConfirm, loading]);

    if (loading || !mappedDebin) {
        return (
            <Box flex justify="center" align="center" column py={64}>
                <Spinner size="large" />
            </Box>
        );
    }

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="debin" />}
            <Box css={{ backgroundColor: "#f9f9f9" }}>
                <Box mx={isDesktop ? 40 : 8} my={isDesktop ? 24 : null} className="admin-detail-head px-0">
                    {(!embeddedInV3Shell || isDesktop) && isDesktop && <Head onBack={handleBack} />}
                    {(!embeddedInV3Shell || isDesktop) && (
                        <Head
                            titleText={`${i18n.get(
                                `debin.programmed.confirmation.title.${debinToConfirm.action.toLowerCase()}`,
                            )} ${mappedDebin.debin.service}`}
                            titleClassName="my-0 align-center"
                            onBack={!isDesktop && handleBack}
                            headerClassName={classNames({ "blue-main-header-mobile": !isDesktop })}
                            rightContent={() => {}}
                        />
                    )}
                    <Box py={isDesktop ? 24 : null}>
                        <DebinScheduledConfirmation
                            headText={i18n.get(
                                `debin.programmed.confirmation.headText.${debinToConfirm.action.toLowerCase()}`,
                            )}
                            debin={mappedDebin}
                            partsList={partsList}
                            getCardLabel={getCardLabel}
                            getTableLabel={getTableLabel}
                            loading={loading}
                            confirmationProps={confirmationProps}
                            {...((debinToConfirm.action.toUpperCase() === "AUTORIZAR" ||
                                debinToConfirm.action.toUpperCase() === "ACTIVAR") && {
                                hintText: i18n.get("debin.programmed.confirmation.hint.text", null, {
                                    AMOUNT: new Intl.NumberFormat("es-AR", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    }).format(mappedDebin.debin.amount),
                                    CURRENCY: i18n.get(`currency.label.${mappedDebin.debin.currency}`),
                                    INSTALLMENTS: mappedDebin.debin.limit_part,
                                    SELLER_NAME: mappedDebin.seller.cuit,
                                }),
                            })}
                        />
                    </Box>
                </Box>
            </Box>
        </>
    );
}

DebinScheduledConfirmationPage.propTypes = {
    dispatch: func.isRequired,
    debinToConfirm: shape({}),
    accounts: arrayOf(shape({})).isRequired,
    loading: bool.isRequired,
    isDesktop: bool.isRequired,
    history: shape({
        replace: func.isRequired,
    }).isRequired,
    seller: shape({}),
    tokenNumber: string,
    embeddedInV3Shell: bool,
};

DebinScheduledConfirmationPage.defaultProps = {
    debinToConfirm: null,
    seller: null,
    tokenNumber: null,
    embeddedInV3Shell: false,
};

const mapStateToProps = (state) => ({
    debinToConfirm: debinSelectors.getRecurrencyDebinAction(state),
    accounts: debinSelectors.getAccounts(state),
    loading: debinSelectors.getFetching(state),
    seller: debinSelectors.getSeller(state),
    tokenNumber: tokenSelectors.getTokenNumber(state),
});

export default withRouter(compose(connect(mapStateToProps), resizableRoute)(DebinScheduledConfirmationPage));
